import axios, { AxiosResponse } from 'axios';
import { apiClient } from './apiClient';
import { FileWithUrl } from './documents';
import { Job } from './jobs';
import { Profile } from './profiles';

export interface User {
    _id: string;
    codeProC: string;
    lastName: string;
    firstName: string;
    jobId: Job;
    username: string;
    profileId: Profile;
    latitude: number;
    longitude: number;
    color: string;
    crudStatus: number;
    address: string;
    isSpecial?: boolean;
    specialType?: number;
    phoneNumber?: string;
    isSST?: boolean;
    securityPassportId?: string;
    securityPassportFile?: FileWithUrl | null;
    arrivalDate?: string;
    departureDate?: string;
    price?: number;
    priceType?: number;
    contractType?: number;
    payrollNumber?: string;
    updatedAt?: string;
    createdAt?: string;
}

export interface UsersResponse {
    data: User[];
    message: string;
}

export interface GetAllUsersQueries {
    includeFiles?: boolean;
    active?: boolean;
}

export const getAllUsers = async (queries: GetAllUsersQueries = {}): Promise<UsersResponse> => {
    try {
        const response: AxiosResponse<UsersResponse> = await apiClient.get('/users', { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateUserBody {
    lastName?: string;
    firstName?: string;
    username?: string;
    address?: string;
    codeProC?: string;
    jobId?: string;
    profileId?: string;
    color?: string;
    crudStatus?: number;
    isSpecial?: boolean;
    specialType?: number | null;
    phoneNumber?: string;
    isSST?: boolean;
    arrivalDate?: string | null;
    departureDate?: string | null;
    longitude?: number | null;
    latitude?: number | null;
    contractType?: number | null;
    payrollNumber?: string | null;
    priceType?: number | null;
    price?: number | null;
}

export interface UserResponse {
    data: User;
    message: string;
}

export const updateUser = async (userId: string, data: UpdateUserBody): Promise<UserResponse> => {
    try {
        const response: AxiosResponse<UserResponse> = await apiClient.patch('/users/' + userId, data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const uploadSecurityPassport = async (userId: string, file: File): Promise<UserResponse> => {
    try {
        const formData = new FormData();
        formData.append('securityPassportFile', file);

        const response: AxiosResponse<UserResponse> = await apiClient.post(`/users/${userId}/security-passport`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const deleteSecurityPassport = async (userId: string): Promise<UserResponse> => {
    try {
        const response: AxiosResponse<UserResponse> = await apiClient.delete(`/users/${userId}/security-passport`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface CreateUserBody {
    lastName: string;
    firstName: string;
    username: string;
    address: string;
    codeProC: string;
    jobId: string;
    profileId: string;
    color: string;
    password: string;
    isSpecial?: boolean;
    specialType?: number | null;
    crudStatus?: number;
    phoneNumber?: string;
    isSST?: boolean;
    arrivalDate?: string | null;
    departureDate?: string | null;
    longitude?: number | null;
    latitude?: number | null;
    contractType?: number | null;
    payrollNumber?: string | null;
    priceType?: number | null;
    price?: number | null;
}

export const createUser = async (data: CreateUserBody): Promise<UserResponse> => {
    try {
        const response: AxiosResponse<UserResponse> = await apiClient.post('/users', data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface ChangePasswordBody {
    newPassword: string;
}

export const changeUserPassword = async (userId: string, data: ChangePasswordBody): Promise<UserResponse> => {
    try {
        const response: AxiosResponse<UserResponse> = await apiClient.patch(`/users/${userId}/password`, data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
